import { throwError } from 'rxjs';
import dayjs from '../services/dayjs.service';

export class DateHelper {
  public static get now(): string {
    return dayjs().toISOString();
  }

  public static max(dates: string[]): string {
    return dayjs.max(dates.map((date: string) => dayjs(date))).toISOString();
  }

  public static min(dates: string[]): string {
    return dayjs.min(dates.map((date: string) => dayjs(date))).toISOString();
  }

  public static diffInMinutes(a: Date | string | dayjs.Dayjs, b: Date | string | dayjs.Dayjs): number {
    return dayjs(a).diff(dayjs(b), 'hours');
  }

  public static diffInSeconds(a: Date | string | dayjs.Dayjs, b: Date | string | dayjs.Dayjs): number {
    return dayjs(a).diff(dayjs(b), 'seconds');
  }

  public static isBefore(a: Date | string | dayjs.Dayjs, b: Date | string | dayjs.Dayjs): boolean {
    return dayjs(a).isBefore(b);
  }

  public static isSameOrBefore(a: Date | string | dayjs.Dayjs, b: Date | string | dayjs.Dayjs): boolean {
    return dayjs(a).isSameOrBefore(b);
  }

  public static isSameOrAfter(a: Date | string | dayjs.Dayjs, b: Date | string | dayjs.Dayjs): boolean {
    return dayjs(a).isSameOrAfter(b);
  }

  public static isAfter(a: Date | string | dayjs.Dayjs, b: Date | string | dayjs.Dayjs): boolean {
    return dayjs(a).isAfter(b);
  }

  public static isSame(a: Date | string | dayjs.Dayjs, b: Date | string | dayjs.Dayjs): boolean {
    return dayjs(a).isSame(b);
  }

  public static getTime(date: string): number {
    return date ? new Date(date).getTime() : new Date(DateHelper.now).getTime();
  }

  public static startOfTheDayByUTC(date: string): string {
    return dayjs.utc(date).startOf('day').toISOString();
  }

  public static startOfTheDay(date: string): string {
    const d = dayjs(date);
    return dayjs
      .utc({
        year: d.year(),
        month: d.month(),
        day: d.date(),
        hour: 0,
        minute: 0,
        second: 0,
      })
      .format();
  }

  public static endOfTheDay(date: string): string {
    const d = dayjs(date);
    return dayjs
      .utc({
        year: d.year(),
        month: d.month(),
        day: d.date(),
        hour: 23,
        minute: 59,
        second: 59,
      })
      .format();
  }

  public static startOfTheNextMonth(date: string, format: string = 'DD/MM/YYYY'): string {
    return dayjs.utc(date).add(1, 'month').startOf('month').format(format).toString();
  }

  public static addDaysByUTC(date: string, days: number): string {
    return dayjs.utc(date).add(days, 'day').toISOString();
  }

  public static yearByUtc(date: string): number {
    return dayjs.utc(date).year();
  }

  public static monthByUtc(date: string): number {
    return dayjs.utc(date).month();
  }

  public static dateByUtc(date: string): number {
    return dayjs.utc(date).date();
  }

  public static hourByUtc(date: string): number {
    return dayjs.utc(date).hour();
  }

  public static minuteByUtc(date: string): number {
    return dayjs.utc(date).minute();
  }

  public static secondByUtc(date: string): number {
    return dayjs.utc(date).second();
  }

  public static newUtcDate(date: {
    year: number;
    month: number;
    day: number;
    hour?: number;
    minute?: number;
    second?: number;
    millisecond?: number;
  }): string {
    if (date) {
      // Look on the issue in Day.js repository. Waiting resolving issue and then remove @ts-ignore.
      // https://github.com/iamkun/dayjs/pull/1647
      // @ts-ignore
      return dayjs.utc(date).toISOString();
    } else {
      throwError('Invalid date format');
    }
  }
}
